'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { LeagueCard } from '@pickleballinc/ui/components/global/Cards/LeagueCard';
import { isAbsoluteUrl } from '@pickleballinc/ui/components/global/utils/isAbsoluteUrl';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useEffect, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { LeagueSearch } from '@/modules/league/new-components/types';
import { LEAGUES_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';
import { checkIfValidUUID } from '@/utils/helpers/uuid';

export interface LeagueCardProps {
  league: LeagueSearch;
  isManageLeague: boolean;
  isManagingCard?: boolean;
}

export const LeagueListCard = ({ league, isManagingCard }: LeagueCardProps) => {
  const { LeagueID, slug, LeagueSessionID, LeagueSessionID_Cur, LeagueSessionID_Nxt } = league;
  const [pbracketsURL, setPbracketsURL] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(`${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
      url.searchParams.set('mf', 'l');
      url.searchParams.set('mid', LeagueID);
      url.searchParams.set('todo', 'm');
      url.searchParams.set('rurl', window.location.href);
      setPbracketsURL(url.toString());
    }
  }, [LeagueID]);

  const internalHref = `${publicEnv.NEXT_PUBLIC_PL_URL}/leagues/${slug || LeagueID}/session/${
    LeagueSessionID || LeagueSessionID_Cur || LeagueSessionID_Nxt
  }?tab=standings`;

  const isUUID = checkIfValidUUID(LeagueID);
  const href = isManagingCard
    ? pbracketsURL
    : isUUID
      ? internalHref
      : `${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/lgd.aspx?lid=${LeagueID}`;
  const isExternal = isManagingCard || !isUUID;

  const rawLogo = league.LeagueLogo || league.ClubLogo;
  // Logos saved through manage are full media-service URLs; legacy ones are bare filenames.
  const logoUrl = rawLogo
    ? isAbsoluteUrl(rawLogo)
      ? rawLogo
      : `${publicEnv.NEXT_PUBLIC_PBRACKETS_CDN_URL}/uploads/clubs/${league.ClubID}/${rawLogo}`
    : LEAGUES_PLACEHOLDER_IMAGE;

  const cardContent = (
    <LeagueCard
      dateFrom={formatDateTime(league.FirstMatchDate)}
      dateTo={formatDateTime(league.LastMatchDate)}
      startTime={
        league.CurMatchDateStartDateTime
          ? `Start: ${formatDateTime(league.CurMatchDateStartDateTime, { type: 'time' })}`
          : ''
      }
      title={league.LeagueTitle}
      location={formatLocation([league.City, league.StateAbbreviation, league.CountryAbbreviation])}
      currency={league.PaymentCurrencyAbbreviation}
      price={league.Cost_Registration_Member}
      formatTag={league.PaymentCurrencyFormatTag}
      logo={logoUrl}
      registrationCount={league.TotalRegistered}
      isLocked={league.IsLocked}
      isRegistrationClosed={league.IsRegClosed === 1}
      clubTitle={league.ClubTitle}
    />
  );

  return (
    <div key={LeagueID} className='flex justify-center w-full'>
      {isExternal ? (
        <a
          href={href}
          aria-label={`Link to ${league.LeagueTitle} league on Pickleballbrackets (Opens in new tab)`}
          target='_blank'
          rel='noopener noreferrer'
          className='relative flex w-full hover:opacity-80'
        >
          {cardContent}
        </a>
      ) : (
        <CustomLink href={href} className='relative flex w-full hover:opacity-80' target='_self'>
          {cardContent}
        </CustomLink>
      )}
    </div>
  );
};
